import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { parseAikenFormat, type AikenParseStats, type ParsedQuestion } from "@/utils/aiken-parser";
import { formatMathExpressions } from "@/utils/formatUtils";

interface PaperUploaderProps {
  onSuccess?: () => void;
  selectedExam?: {
    name: string;
    type: string;
  };
  selectedSection?: {
    name: string;
  };
}

const SafeDisplay = ({ content }: { content: string }) => {
  return <div dangerouslySetInnerHTML={{ __html: formatMathExpressions(content) }} />;
};

export const PaperUploader = ({ onSuccess, selectedExam, selectedSection }: PaperUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [examDetails, setExamDetails] = useState({
    examName: selectedExam?.name || "",
    subject: selectedSection?.name || "",
    setName: "",
  });
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [parseStats, setParseStats] = useState<AikenParseStats | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const [isCompletePaper, setIsCompletePaper] = useState(false);

  React.useEffect(() => {
    setExamDetails(prev => ({
      ...prev,
      examName: selectedExam?.name || prev.examName,
      subject: selectedSection?.name || prev.subject,
    }));
  }, [selectedExam, selectedSection]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    if (selectedFile.type === "text/plain" || selectedFile.name.endsWith('.txt')) {
      setFile(selectedFile);
      try {
        const content = await selectedFile.text();
        setFileContent(content);
        console.log("File content sample:", content.substring(0, 200));
        
        const { questions, stats } = parseAikenFormat(content, examDetails, selectedExam, selectedSection);
        
        if (questions.length === 0) {
          toast.error("No valid questions found in the file. Please check the format.");
          setParsedQuestions([]);
          setParseStats(stats);
          return;
        }
        
        setParsedQuestions(questions);
        setParseStats(stats);
        toast.success(`File parsed successfully: ${questions.length} questions found`);
        setShowPreview(true);
      } catch (error) {
        console.error("Error parsing file:", error);
        toast.error("Failed to parse the file. Please check the format.");
      }
    } else {
      toast.error("Please select a valid text file in Aiken format");
    }
  };

  const handleUpload = async () => {
    if (!file || !examDetails.examName) {
      toast.error("Please fill in all required fields");
      return;
    }

    // For complete papers, subject field is not required
    if (!isCompletePaper && !examDetails.subject) {
      toast.error("Please provide a subject name for sectional test");
      return;
    }

    if (parsedQuestions.length === 0) {
      toast.error("No valid questions to import");
      return;
    }

    setIsUploading(true);
    try {
      const questions = parsedQuestions;
      
      const BATCH_SIZE = 20;
      let successCount = 0;
      
      for (let i = 0; i < questions.length; i += BATCH_SIZE) {
        const batch = questions.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('mock_test_questions').insert(batch);
        
        if (error) {
          console.error('Batch insert error:', error);
          toast.error(`Error inserting batch ${i/BATCH_SIZE + 1}: ${error.message}`);
        } else {
          successCount += batch.length;
          toast.success(`Imported batch ${i/BATCH_SIZE + 1} (${successCount}/${questions.length} questions)`);
        }
      }

      toast.success(`Successfully imported ${successCount} questions`);
      setFile(null);
      setParsedQuestions([]);
      setShowPreview(false);
      setParseStats(null);
      onSuccess?.();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to import questions: " + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xs:grid-cols-1 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="examName">Exam Name *</Label>
          <Input
            id="examName"
            value={examDetails.examName}
            onChange={(e) => setExamDetails(prev => ({ ...prev, examName: e.target.value }))}
            placeholder="e.g., CDS"
            disabled={!!selectedExam}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="paperType" className="flex items-center gap-2">
            <input 
              type="checkbox" 
              id="paperType" 
              checked={isCompletePaper}
              onChange={() => setIsCompletePaper(!isCompletePaper)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span>Complete Paper (No sections)</span>
          </Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="subject">
            Subject {!isCompletePaper && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id="subject"
            value={examDetails.subject}
            onChange={(e) => setExamDetails(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="e.g., English"
            disabled={!!selectedSection}
            required={!isCompletePaper}
          />
          {isCompletePaper && (
            <p className="text-xs text-muted-foreground">Not required for complete papers</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="setName">Set Name</Label>
          <Input
            id="setName"
            value={examDetails.setName}
            onChange={(e) => setExamDetails(prev => ({ ...prev, setName: e.target.value }))}
            placeholder="e.g., Set II"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="txtFile">Upload Questions File (Aiken Format) *</Label>
        <Input
          id="txtFile"
          type="file"
          accept=".txt"
          onChange={handleFileChange}
          className="cursor-pointer"
        />
        {file && (
          <p className="text-sm text-muted-foreground mt-1">
            Selected file: {file.name}
          </p>
        )}
        <div className="text-xs text-muted-foreground space-y-1 mt-2">
          <p className="font-medium">Required Aiken Format:</p>
          <div className="bg-muted p-2 rounded-md font-mono text-[11px] whitespace-pre">
{`# Simple questions
My teacher was _____ us for being late.
A. annoyed about
B. annoyed with
C. annoys
D. annoyed at
E. annoying with
ANSWER: B

# Reading passage (a blank line before and after)
Daily consumption of a certain form of curcumin improved memory and mood in people with mild, age-related memory loss. The research examined the effects of an easily absorbed curcumin supplement on memory performance in people without dementia, as well as curcumin's potential impact on the microscopic plaques and tangled in the brains of people with Alzheimer's disease.

Which of the following statements are true regarding curcumin?
A. 2, 3 and 4
B. 1 and 4 only
C. 1, 3 and 4
D. 1 and 3 only
ANSWER: A`}</div>
          <p className="mt-1 text-amber-600">For reading comprehension questions, include the passage before the questions.</p>
          <p className="text-amber-600">Make sure to follow the exact format with A., B., C., D. options (E. is optional for 5-option questions)</p>
          <p className="text-amber-600">Use ANSWER: followed by the correct option letter (A, B, C, D, or E)</p>
        </div>
      </div>

      {parseStats && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-medium text-amber-800">File Statistics</h4>
              <ul className="space-y-1 text-amber-700">
                <li>Total lines: {parseStats.totalLines}</li>
                <li>Valid questions: {parseStats.validQuestions}</li>
                <li>Skipped rows: {parseStats.invalidRows}</li>
                {Object.entries(parseStats.skippedReasons).length > 0 && (
                  <li>
                    <p className="font-medium">Reasons for skipping:</p>
                    <ul className="pl-4 mt-1 space-y-1 list-disc">
                      {Object.entries(parseStats.skippedReasons).map(([reason, count]) => (
                        <li key={reason}>{reason}: {count} row(s)</li>
                      ))}
                    </ul>
                  </li>
                )}
                {parseStats.skippedRows.length > 0 && (
                  <li>
                    <p className="font-medium mt-2">Skipped rows details (first 10):</p>
                    <ul className="pl-4 mt-1 space-y-1 list-disc max-h-40 overflow-y-auto">
                      {parseStats.skippedRows.slice(0, 10).map((skipped, i) => (
                        <li key={i}>Row {skipped.rowIndex + 1}: {skipped.reason}</li>
                      ))}
                      {parseStats.skippedRows.length > 10 && (
                        <li className="text-amber-600">...and {parseStats.skippedRows.length - 10} more</li>
                      )}
                    </ul>
                  </li>
                )}
              </ul>
              <div className="mt-3 p-2 bg-amber-100 rounded-md">
                <p className="font-medium text-amber-800">Format Help:</p>
                <ul className="pl-4 mt-1 space-y-1 list-disc text-amber-700">
                  <li>Make sure to follow the exact format with A), B), C), D) options (E. is optional for 5-option questions)</li>
                  <li>Each question must have all four options (A, B, C, D)</li>
                  <li>The ANSWER: line must appear immediately after the options</li>
                  <li>The correct answer must be a single letter (A, B, C, or D)</li>
                  <li>A blank line should separate each question</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              className="mt-4"
              disabled={parsedQuestions.length === 0}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview {parsedQuestions.length > 0 ? `${parsedQuestions.length} Questions` : "Questions"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Question Import Preview</DialogTitle>
              <DialogDescription>
                Review the parsed questions before importing them
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>No.</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Option A</TableHead>
                    <TableHead>Option B</TableHead>
                    <TableHead>Option C</TableHead>
                    <TableHead>Option D</TableHead>
                    <TableHead>Option E</TableHead>
                    <TableHead>Correct</TableHead>
                    <TableHead>Passage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedQuestions.map((question, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        <SafeDisplay content={question.question_text} />
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        <SafeDisplay content={question.option_a} />
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        <SafeDisplay content={question.option_b} />
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        <SafeDisplay content={question.option_c} />
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        <SafeDisplay content={question.option_d} />
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {question.option_e ? <SafeDisplay content={question.option_e} /> : <span className="text-xs text-gray-400">N/A</span>}
                      </TableCell>
                      <TableCell>{question.correct_option.toUpperCase()}</TableCell>
                      <TableCell>{question.metadata.passage ? 
                        <span className="text-xs text-green-600">Yes</span> : 
                        <span className="text-xs text-gray-400">No</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        <Button 
          onClick={handleUpload} 
          disabled={isUploading || !file || parsedQuestions.length === 0 || (!isCompletePaper && !examDetails.subject)}
          className="w-full mt-4"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Import Questions
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
